import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ModalComponent } from '../../../shared/components/ui/modal/modal.component';
import { ToastService } from '../../../shared/services/toast.service';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';
import { DatetimeService } from '../../../shared/services/datetime.service';
import { AccessRequestAdminApiService } from '../services/access-request-api.service';
import { InvitationApiService } from '../../invitations/services/invitation-api.service';
import { RoleResponse } from '../../invitations/models/invitation.model';
import {
  ACCESS_REQUEST_STATUS_LABEL,
  AccessRequest,
  AccessRequestStatus,
} from '../models/access-request.model';
import { PAGE_SIZE_OPTIONS_DEFAULT } from '../../../shared/utils/pagination.constants';

/**
 * Bandeja de solicitudes de acceso.
 *
 * <p>Partner CRM se vende de forma asistida: no hay alta de auto-servicio. Quien
 * no tiene invitación deja una solicitud en `/signup` y aquí la atiende Super
 * Admin. La tabla es global (sin businessId: quien solicita no pertenece a
 * ninguna empresa todavía), así que a diferencia de Invitaciones no comparte el
 * gate del módulo `users` — Admin/Manager de un cliente verían prospectos de
 * otras empresas si se les diera acceso.</p>
 *
 * <p>La acción principal es <b>Invitar</b>: se elige el rol y se crea la
 * invitación con el endpoint de siempre (`POST /api/invitations`), de modo que la
 * persona entra por el flujo `/invite/:token` que ya existía. Se orquesta desde
 * aquí en dos llamadas — invitar y marcar CONVERTIDA — en vez de añadir un
 * endpoint nuevo que acoplaría el módulo de solicitudes al de invitaciones. Si la
 * segunda falla, la invitación ya salió y la solicitud simplemente sigue visible
 * como pendiente, que es el fallo menos malo de los dos.</p>
 *
 * <p>El backend aplica un techo de rol: no se puede invitar con un rol superior al
 * propio. Un Manager no verá Admin ni Super Admin como opciones válidas y, si lo
 * intentara, recibiría 403.</p>
 */
@Component({
  selector: 'app-access-request-list',
  standalone: true,
  imports: [CommonModule, FormsModule, PageBreadcrumbComponent, ModalComponent],
  templateUrl: './access-request-list.component.html',
})
export class AccessRequestListComponent implements OnInit, OnDestroy {
  private readonly api = inject(AccessRequestAdminApiService);
  private readonly invitationApi = inject(InvitationApiService);
  private readonly toast = inject(ToastService);
  private readonly confirmDialog = inject(ConfirmDialogService);
  private readonly datetimeService = inject(DatetimeService);

  readonly STATUS_LABEL = ACCESS_REQUEST_STATUS_LABEL;
  readonly STATUSES: AccessRequestStatus[] = [
    'NEW',
    'CONTACTED',
    'CONVERTED',
    'REJECTED',
  ];

  requests: AccessRequest[] = [];
  loading = false;
  error = '';

  // Paginación (el endpoint devuelve una lista simple, no un PageResponse:
  // el volumen esperado es bajo y no había motivo para paginar en el servidor).
  page = 0;
  readonly PAGE_SIZE_OPTIONS = PAGE_SIZE_OPTIONS_DEFAULT;
  private readonly PAGE_SIZE_KEY = 'access-request-list-page-size';
  pageSize: number = Number(localStorage.getItem(this.PAGE_SIZE_KEY)) || 20;

  statusFilter: AccessRequestStatus | '' = '';
  pendingCount = 0;

  // Detalle
  detailRequest: AccessRequest | null = null;

  // Invitar
  inviteRequest: AccessRequest | null = null;
  inviteRoleId: number | null = null;
  inviteSaving = false;
  roles: RoleResponse[] = [];
  rolesLoading = false;

  // Cambiar estado
  statusRequest: AccessRequest | null = null;
  statusValue: AccessRequestStatus = 'CONTACTED';
  statusNotes = '';
  statusSaving = false;

  private readonly destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.load();
    this.loadRoles();
    this.refreshPendingCount();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Carga ───────────────────────────────────────────────────────────────────

  load(): void {
    this.loading = true;
    this.error = '';
    this.api
      .list({
        status: this.statusFilter || undefined,
        page: this.page,
        size: this.pageSize,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (rows) => {
          this.requests = rows;
          this.loading = false;
        },
        error: (err) => {
          this.loading = false;
          this.error =
            err?.status === 403
              ? 'No tienes permisos para ver las solicitudes de acceso.'
              : err?.error?.message || 'No se pudieron cargar las solicitudes.';
        },
      });
  }

  private loadRoles(): void {
    this.rolesLoading = true;
    this.invitationApi
      .listRoles()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (roles) => {
          this.roles = roles;
          this.rolesLoading = false;
        },
        error: () => {
          this.rolesLoading = false;
        },
      });
  }

  private refreshPendingCount(): void {
    this.api
      .countByStatus('NEW')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (n) => (this.pendingCount = n),
        error: () => (this.pendingCount = 0),
      });
  }

  onFilterChange(): void {
    this.page = 0;
    this.load();
  }

  onPageSizeChange(): void {
    localStorage.setItem(this.PAGE_SIZE_KEY, String(this.pageSize));
    this.page = 0;
    this.load();
  }

  prevPage(): void {
    if (this.page > 0) {
      this.page--;
      this.load();
    }
  }

  nextPage(): void {
    // El endpoint no devuelve total: si la página vino llena, asumimos que hay más.
    if (this.requests.length === this.pageSize) {
      this.page++;
      this.load();
    }
  }

  get hasNextPage(): boolean {
    return this.requests.length === this.pageSize;
  }

  // ── Detalle ─────────────────────────────────────────────────────────────────

  openDetail(request: AccessRequest): void {
    this.detailRequest = request;
  }

  closeDetail(): void {
    this.detailRequest = null;
  }

  // ── Invitar ─────────────────────────────────────────────────────────────────

  openInvite(request: AccessRequest): void {
    this.inviteRequest = request;
    this.inviteRoleId = null;
  }

  closeInvite(): void {
    this.inviteRequest = null;
    this.inviteRoleId = null;
    this.inviteSaving = false;
  }

  confirmInvite(): void {
    if (!this.inviteRequest || this.inviteRoleId === null || this.inviteSaving) {
      return;
    }
    const request = this.inviteRequest;
    this.inviteSaving = true;

    this.invitationApi
      .createInvitation({
        mailInvited: request.email,
        roleId: this.inviteRoleId,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          // La invitación ya salió. Marcar la solicitud es un paso aparte: si
          // fallara, no se reintenta el envío (duplicaría el correo), solo se avisa.
          this.api
            .updateStatus(request.id, {
              status: 'CONVERTED',
              notes: this.appendNote(
                request.notes,
                'Invitación enviada desde la bandeja de solicitudes.',
              ),
            })
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: () => {
                this.inviteSaving = false;
                this.closeInvite();
                this.toast.success(
                  `Invitación enviada a ${request.email}.`,
                );
                this.load();
                this.refreshPendingCount();
              },
              error: () => {
                this.inviteSaving = false;
                this.closeInvite();
                this.toast.warning(
                  `Se envió la invitación a ${request.email}, pero la solicitud no se marcó como convertida. Cámbiala a mano.`,
                );
                this.load();
              },
            });
        },
        error: (err) => {
          this.inviteSaving = false;
          const message =
            err?.status === 403
              ? 'No puedes asignar ese rol: está por encima del tuyo.'
              : err?.status === 409
                ? 'Ya existe una invitación pendiente o el correo ya está registrado en tu empresa.'
                : err?.error?.message || 'No se pudo enviar la invitación.';
          this.toast.error(message);
        },
      });
  }

  // ── Cambiar estado ──────────────────────────────────────────────────────────

  openStatus(request: AccessRequest, preset: AccessRequestStatus): void {
    this.statusRequest = request;
    this.statusValue = preset;
    this.statusNotes = '';
  }

  closeStatus(): void {
    this.statusRequest = null;
    this.statusNotes = '';
    this.statusSaving = false;
  }

  confirmStatus(): void {
    if (!this.statusRequest || this.statusSaving) {
      return;
    }
    const request = this.statusRequest;
    this.statusSaving = true;

    this.api
      .updateStatus(request.id, {
        status: this.statusValue,
        notes: this.statusNotes.trim()
          ? this.appendNote(request.notes, this.statusNotes.trim())
          : undefined,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.statusSaving = false;
          this.closeStatus();
          this.toast.updated('Solicitud');
          this.load();
          this.refreshPendingCount();
        },
        error: (err) => {
          this.statusSaving = false;
          this.toast.error(
            err?.error?.message || 'No se pudo actualizar la solicitud.',
          );
        },
      });
  }

  reject(request: AccessRequest): void {
    this.confirmDialog
      .confirm({
        title: 'Rechazar solicitud',
        message: `¿Rechazar la solicitud de ${request.companyName} (${request.email})?`,
        confirmText: 'Rechazar',
        cancelText: 'Cancelar',
        variant: 'danger',
      })
      .then((ok) => {
        if (!ok) {
          return;
        }
        this.api
          .updateStatus(request.id, { status: 'REJECTED' })
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.toast.updated('Solicitud');
              this.load();
              this.refreshPendingCount();
            },
            error: (err) =>
              this.toast.error(
                err?.error?.message || 'No se pudo rechazar la solicitud.',
              ),
          });
      });
  }

  // ── Helpers de presentación ─────────────────────────────────────────────────

  statusClass(status: AccessRequestStatus): string {
    switch (status) {
      case 'NEW':
        return 'bg-brand-50 text-brand-600 dark:bg-brand-500/15 dark:text-brand-400';
      case 'CONTACTED':
        return 'bg-warning-50 text-warning-600 dark:bg-warning-500/15 dark:text-warning-400';
      case 'CONVERTED':
        return 'bg-success-50 text-success-600 dark:bg-success-500/15 dark:text-success-400';
      case 'REJECTED':
        return 'bg-error-50 text-error-600 dark:bg-error-500/15 dark:text-error-400';
      default:
        return 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300';
    }
  }

  formatDate(value: string | null | undefined): string {
    return value ? this.datetimeService.formatDateTime(value) : '—';
  }

  /** Acumula notas en vez de pisar la anterior: el historial comercial importa. */
  private appendNote(existing: string | null | undefined, addition: string): string {
    return existing && existing.trim() ? `${existing}\n${addition}` : addition;
  }
}
